import { useEffect, useState } from "react";
import { Phone, Mail } from "lucide-react";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "./Nav";

/**
 * Sticky bottom contact bar.
 * Desktop / tablet: full-width dark strip fixed to bottom with both actions.
 * Mobile: floating pill-shaped "Call Now" CTA (right) + email icon (left) that pulses gently after the user scrolls past the hero.
 */
export default function StickyContactBar() {
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const onScroll = () => setPulse(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Desktop / tablet — full-width bar */}
      <div
        data-testid="sticky-contact-bar"
        className="hidden sm:block fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-[#060913] text-[#F4F4F0] shadow-[0_-8px_24px_rgba(6,9,19,0.15)]"
      >
        <div className="mx-auto max-w-[1400px] grid grid-cols-2">
          <a
            href={PHONE_HREF}
            data-testid="sticky-phone"
            className="group flex items-center justify-center gap-3 py-4 border-r border-white/10 hover:bg-[#D92323] transition-colors"
          >
            <Phone size={16} strokeWidth={1.5} className="text-[#D92323] group-hover:text-white transition-colors" />
            <span className="font-mono text-xs md:text-sm tracking-[0.18em] uppercase">
              {PHONE}
            </span>
          </a>
          <a
            href={EMAIL_HREF}
            data-testid="sticky-email"
            className="group flex items-center justify-center gap-3 py-4 hover:bg-[#D92323] transition-colors"
          >
            <Mail size={16} strokeWidth={1.5} className="text-[#D92323] group-hover:text-white transition-colors" />
            <span className="font-mono text-xs md:text-sm tracking-[0.14em] lowercase">
              {EMAIL}
            </span>
          </a>
        </div>
      </div>

      {/* Mobile — floating pill CTA with pulse */}
      <div className="sm:hidden fixed bottom-4 left-4 right-4 z-40 flex items-center gap-3 justify-end pointer-events-none">
        <a
          href={EMAIL_HREF}
          data-testid="sticky-email-mobile"
          className="pointer-events-auto inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#060913] text-white shadow-lg border border-white/10"
          aria-label="Email us"
        >
          <Mail size={18} strokeWidth={1.7} />
        </a>
        <a
          href={PHONE_HREF}
          data-testid="sticky-phone-mobile"
          className={`pointer-events-auto relative inline-flex items-center gap-2.5 rounded-full bg-[#D92323] text-white pl-4 pr-5 py-3.5 shadow-xl font-mono text-[12px] tracking-[0.14em] uppercase ${pulse ? "bc-pulse" : ""}`}
        >
          <Phone size={16} strokeWidth={1.8} />
          Call Now
        </a>
      </div>

      <style>{`
        @keyframes bcPulse {
          0%   { box-shadow: 0 10px 24px rgba(217,35,35,0.35), 0 0 0 0 rgba(217,35,35,0.55); }
          70%  { box-shadow: 0 10px 24px rgba(217,35,35,0.35), 0 0 0 14px rgba(217,35,35,0); }
          100% { box-shadow: 0 10px 24px rgba(217,35,35,0.35), 0 0 0 0 rgba(217,35,35,0); }
        }
        .bc-pulse {
          animation: bcPulse 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .bc-pulse { animation: none; }
        }
      `}</style>
    </>
  );
}
